'use client';

import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GlassmorphicCta } from '@odys/design-system/components/GlassmorphicCta';
import { radius } from '@odys/design-system/tokens/radius';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import type { DestinationDto } from '@/app/api/lib/destinations/destination.types';
import type { DmcCardDto, DmcVisitorDto } from '@/app/api/lib/dmcs/dmc.types';
import type { Role } from '@/app/api/lib/permissions/matrix';
import {
    DEFAULT_FILTERS,
    DestinationFilters,
    type DestinationFiltersValue,
    applyDestinationFilters,
} from '@/components/destinations/DestinationFilters';
import { DmcCard } from '@/components/destinations/DmcCard';
import { NAVBAR_OFFSET } from '@/components/layout/navbar/navbar-offset';
import { Link } from '@/configurations/i18n/navigation';

type DestinationDetailProps = {
    destination: DestinationDto;
    dmcs: Array<DmcCardDto | DmcVisitorDto>;
    viewerRole: Role;
};

export function DestinationDetail({ destination, dmcs, viewerRole }: DestinationDetailProps) {
    const t = useTranslations('destinationDetail');

    return (
        <>
            <DestinationHero destination={destination} />
            <Container maxWidth="lg" sx={{ pt: 4, pb: 7 }}>
                <Stack spacing={3}>
                    <Typography variant="h2" component="h2">
                        {t('dmcSectionTitle', { destination: destination.name })}
                    </Typography>
                    <DmcSection
                        destinationSlug={destination.slug}
                        dmcs={dmcs}
                        viewerRole={viewerRole}
                    />
                    {viewerRole === 'visitor' && (
                        <DestinationVisitorCta backgroundImageUrl={destination.imageUrl} />
                    )}
                </Stack>
            </Container>
        </>
    );
}

function DmcSection({
    destinationSlug,
    dmcs,
    viewerRole,
}: {
    destinationSlug: string;
    dmcs: ReadonlyArray<DmcCardDto | DmcVisitorDto>;
    viewerRole: Role;
}) {
    const [filters, setFilters] = useState<DestinationFiltersValue>(DEFAULT_FILTERS);

    const visibleDmcs = useMemo(() => applyDestinationFilters(dmcs, filters), [dmcs, filters]);

    if (dmcs.length === 0) {
        return <EmptyState testId="destination-empty" messageKey="empty" />;
    }

    return (
        <>
            <DestinationFilters
                destinationSlug={destinationSlug}
                value={filters}
                onChange={setFilters}
                dmcs={dmcs}
                canSortByProducts={viewerRole === 'agency'}
            />

            {visibleDmcs.length === 0 ? (
                <EmptyState testId="destination-no-match" messageKey="emptyFiltered" />
            ) : (
                <Stack spacing={3} data-testid="destination-dmc-cards">
                    {visibleDmcs.map((dmc) => (
                        <DmcCard key={dmc.id} dmc={dmc} />
                    ))}
                </Stack>
            )}
        </>
    );
}

/**
 * Hero plein-écran avec image de destination, blur à 50px sur les 3/5 gauches
 * pour rendre le titre (h2 60px light) et le breadcrumb lisibles. Pas de
 * Container — édge-to-édge pour matcher le Figma.
 *
 * La description éditoriale (admin, par locale) se glisse sous le h1 quand
 * elle existe ; absente ou vide, aucun bloc n'est rendu.
 */
function DestinationHero({ destination }: { destination: DestinationDto }) {
    const t = useTranslations('destinationDetail');
    return (
        <Box
            data-testid="destination-hero"
            data-image={destination.imageUrl ? 'present' : 'placeholder'}
            sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 380, md: 500 },
                mt: `-${NAVBAR_OFFSET}px`,
                overflow: 'hidden',
                bgcolor: destination.imageUrl ? 'grey.300' : 'primary.dark',
            }}
        >
            <Box
                data-navbar-hero-sentinel
                aria-hidden
                sx={{ position: 'absolute', top: 0, left: 0, width: 1, height: 1 }}
            />
            {destination.imageUrl && (
                <Image
                    src={destination.imageUrl}
                    alt=""
                    fill
                    priority
                    sizes="100vw"
                    style={{ objectFit: 'cover' }}
                />
            )}
            {/* Glass panel — 3/5 gauches sur md+, 100% sur xs. Mask = blur plein à gauche
                qui fond vers transparent à droite pour un raccord doux avec l'image nette. */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: { xs: '100%', md: '60%' },
                    backdropFilter: 'blur(50px)',
                    WebkitBackdropFilter: 'blur(50px)',
                    background: (theme) =>
                        `linear-gradient(270deg, ${alpha(theme.palette.common.white, 0)} 0%, ${alpha(theme.palette.common.white, 0.1)} 100%)`,
                    maskImage: 'linear-gradient(to right, black 0%, black 70%, transparent 100%)',
                    WebkitMaskImage:
                        'linear-gradient(to right, black 0%, black 70%, transparent 100%)',
                }}
            />
            <Container
                maxWidth="lg"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pt: `${NAVBAR_OFFSET}px`,
                }}
            >
                <Stack spacing={1} sx={{ color: 'common.white', maxWidth: { md: '50%' } }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        aria-label="breadcrumb"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            component={Link}
                            href="/"
                            sx={{
                                color: (theme) => alpha(theme.palette.common.white, 0.8),
                                textDecoration: 'none',
                                '&:hover': { color: 'common.white' },
                            }}
                        >
                            {t('breadcrumbHome')}
                        </Box>
                        <Box
                            component="span"
                            sx={{ color: (theme) => alpha(theme.palette.common.white, 0.6) }}
                        >
                            /
                        </Box>
                        <Box component="span" sx={{ color: 'common.white' }}>
                            {destination.name}
                        </Box>
                    </Stack>
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            color: 'common.white',
                        }}
                    >
                        {destination.name}
                    </Typography>
                    {destination.description && (
                        <Typography
                            data-testid="destination-description"
                            variant="body1"
                            sx={{
                                color: (theme) => alpha(theme.palette.common.white, 0.9),
                                whiteSpace: 'pre-line',
                                mt: 1,
                            }}
                        >
                            {destination.description}
                        </Typography>
                    )}
                </Stack>
            </Container>
        </Box>
    );
}

function EmptyState({
    testId,
    messageKey,
}: {
    testId: string;
    messageKey: 'empty' | 'emptyFiltered';
}) {
    const t = useTranslations('destinationDetail');
    return (
        <Box
            data-testid={testId}
            sx={{
                p: 5,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: radius.lg,
                textAlign: 'center',
            }}
        >
            <Typography
                variant="body1"
                sx={{
                    color: 'text.secondary',
                }}
            >
                {t(messageKey)}
            </Typography>
        </Box>
    );
}

/**
 * CTA glassmorphique à pied de page (visitor only) — image de destination
 * en fond + backdrop-blur, texte blanc, bouton teal.
 */
function DestinationVisitorCta({ backgroundImageUrl }: { backgroundImageUrl: string | null }) {
    const t = useTranslations('destinationDetail.visitorCta');
    return (
        <GlassmorphicCta
            data-testid="destination-visitor-cta"
            heading={t('heading')}
            message={t('message')}
            backgroundImageUrl={backgroundImageUrl}
            action={
                <Button component={Link} href="/sign-up" variant="contained">
                    {t('button')}
                </Button>
            }
        />
    );
}
